import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';

/**
 * Extrait le délai avant retry depuis Retry-After (secondes ou date HTTP)
 * ou RateLimit-Reset (timestamp Unix) renvoyés par express-rate-limit.
 */
export function getRetryAfterSeconds(headers: HttpHeaders | null | undefined): number | null {
  if (!headers) {
    return null;
  }

  const retryAfter = headers.get('Retry-After');
  if (retryAfter) {
    const asNumber = parseInt(retryAfter, 10);
    if (!Number.isNaN(asNumber)) {
      return Math.max(0, asNumber);
    }

    const retryDate = new Date(retryAfter);
    if (!Number.isNaN(retryDate.getTime())) {
      return Math.max(0, Math.ceil((retryDate.getTime() - Date.now()) / 1000));
    }
  }

  const rateLimitReset = headers.get('RateLimit-Reset');
  if (rateLimitReset) {
    const resetTimestamp = parseInt(rateLimitReset, 10);
    if (!Number.isNaN(resetTimestamp)) {
      return Math.max(0, resetTimestamp - Math.floor(Date.now() / 1000));
    }
  }

  return null;
}

/**
 * Délai avant retry : headers HTTP (rate-limit) ou corps API (ex. loginLockout retryAfterMinutes).
 */
export function getRetryAfterSecondsFromError(
  error: Pick<HttpErrorResponse, 'headers' | 'error'> | null | undefined
): number | null {
  if (!error) {
    return null;
  }

  const body = error.error as { retryAfterMinutes?: number } | null | undefined;
  if (typeof body?.retryAfterMinutes === 'number' && body.retryAfterMinutes > 0) {
    return Math.ceil(body.retryAfterMinutes * 60);
  }

  return getRetryAfterSeconds(error.headers);
}

/** Formate une durée en secondes pour affichage utilisateur (FR). */
export function formatDurationFrench(totalSeconds: number): string {
  const seconds = Math.max(0, Math.ceil(totalSeconds));

  if (seconds < 60) {
    return seconds <= 1 ? '1 seconde' : `${seconds} secondes`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    const minPart = minutes > 0 ? ` ${minutes} min` : '';
    return `${hours} h${minPart}`.trim();
  }

  if (remainingSeconds > 0) {
    return `${minutes} min ${remainingSeconds} s`;
  }

  return minutes <= 1 ? '1 minute' : `${minutes} minutes`;
}

/**
 * Retire un éventuel suffixe « Réessayez dans … » déjà présent dans le message API
 * (ex. lockout 423) pour éviter le doublon avec le compte à rebours front.
 */
export function stripRetryAfterSuffix(message: string): string {
  return String(message || '')
    .replace(/\s*Réessayez dans[^.]*\.?\s*$/i, '')
    .trim();
}

/** Message rate limit enrichi avec le délai de retry si disponible. */
export function buildRateLimitMessage(
  baseMessage: string,
  headers: HttpHeaders | null | undefined,
  fallbackSeconds = 15 * 60
): string {
  const retrySeconds = getRetryAfterSeconds(headers) ?? fallbackSeconds;
  const duration = formatDurationFrench(retrySeconds);
  const cleaned = stripRetryAfterSuffix(baseMessage);
  return `${cleaned} Réessayez dans ${duration}.`;
}
